import React, { useState, useEffect, useRef } from 'react';
import { generateClient } from 'aws-amplify/data';
import { sanitise } from '../../utils/sanitise';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import { deriveLifecycle, catchRefusalMessage, CLUB_TIMEZONE } from '@shared/competitionRules';
import { isLengthUnusual, isOverMaxLength, MAX_CATCH_LENGTH_MESSAGE } from '@shared/catchLength';
import LengthConfirmDialog from '../LengthConfirmDialog';
import { formatClubDate, formatClubDateTime } from '../../utils/clubTime';

const client = generateClient({ authMode: 'apiKey' });

const SPECIES_OPTIONS = [
  { value: 'FLATHEAD', label: 'Flathead' },
  { value: 'BREAM', label: 'Bream' },
  { value: 'JEWFISH', label: 'Jewfish' },
  { value: 'MANGROVE_JACK', label: 'Mangrove Jack' },
  { value: 'WHITING', label: 'Whiting' },
  { value: 'TAILOR', label: 'Tailor' },
  { value: 'TREVALLY', label: 'Trevally' },
  { value: 'OTHER', label: 'Other' },
];

const LEGAL_CATCH_SIZES: Record<string, number> = {
  FLATHEAD: 36,
  BREAM: 25,
  JEWFISH: 70,
  MANGROVE_JACK: 35,
  WHITING: 27,
  TAILOR: 30,
  TREVALLY: 25,
};

const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

interface GuestCatchSubmissionProps {
  /**
   * When set, skips the competition dropdown entirely and submits against
   * this event — used by CatchLandingPage, which has already resolved the
   * active competition. When unset, behaves exactly as it does today
   * (self-loads OPEN competitions and shows the picker) — the existing
   * /submit-catch route continues to work unchanged.
   */
  preScopedEventId?: string;
}

const GuestCatchSubmission: React.FC<GuestCatchSubmissionProps> = ({ preScopedEventId }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [competitions, setCompetitions] = useState<any[]>([]);
  const [selectedEventId, setSelectedEventId] = useState(preScopedEventId || '');
  const [preScopedCompTitle, setPreScopedCompTitle] = useState<string | null>(null);
  const [participantEmail, setParticipantEmail] = useState('');
  const [species, setSpecies] = useState('');
  const [lengthCm, setLengthCm] = useState('');
  // #85: confirm step for an unusually long catch; hard cap blocks submit.
  const [showLengthConfirm, setShowLengthConfirm] = useState(false);
  const lengthInputRef = useRef<HTMLInputElement>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [keptOrReleased, setKeptOrReleased] = useState<'KEPT' | 'RELEASED'>('RELEASED');
  const [timeOfCatch, setTimeOfCatch] = useState(formatDateForInput(new Date()));
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);
  const [submitted, setSubmitted] = useState(false);
  // Event timing for the submission-window gate — the guest counterpart of the
  // gate in MemberCatchSubmission. Held null until loaded; the gate treats
  // not-yet-known and failed-to-load alike as closed, matching the server's
  // fail-closed server-side gate.
  const [eventTiming, setEventTiming] = useState<{
    competitionStartTime?: string | null;
    competitionEndTime?: string | null;
    gracePeriodMinutes?: number | null;
    adjudicatedAt?: string | null;
    cancelledAt?: string | null;
  } | null>(null);
  const [timingLoaded, setTimingLoaded] = useState(false);
  // Re-evaluated so the form opens by itself at fishing start rather than
  // needing a guest on the bank to reload the page.
  const [nowTick, setNowTick] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setNowTick(new Date()), 20_000);
    return () => clearInterval(t);
  }, []);

  // Track timing for whichever competition is actually selected — the
  // pre-scoped one from the QR link, or the one picked from the list.
  useEffect(() => {
    if (!selectedEventId) {
      setEventTiming(null);
      setTimingLoaded(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await client.models.Event.get({ id: selectedEventId });
        if (!cancelled && data) {
          setEventTiming({
            competitionStartTime: data.competitionStartTime,
            competitionEndTime: data.competitionEndTime,
            gracePeriodMinutes: data.gracePeriodMinutes,
            adjudicatedAt: (data as any).adjudicatedAt,
            cancelledAt: (data as any).cancelledAt,
          });
        }
      } catch (err) {
        console.error('Error loading competition timing:', err);
      } finally {
        if (!cancelled) setTimingLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedEventId]);

  useEffect(() => {
    if (preScopedEventId) {
      // Competition already known — skip the picker entirely and fetch only
      // the title, for display. A failure here is cosmetic-only: selectedEventId
      // is already set from the prop, so it must never block submission.
      setLoading(false);
      (async () => {
        try {
          const { data } = await client.models.Event.get({ id: preScopedEventId });
          if (data?.title) setPreScopedCompTitle(data.title);
        } catch (err) {
          console.error('Error loading pre-scoped competition title (non-fatal):', err);
        }
      })();
      return;
    }
    loadCompetitions();
  }, [preScopedEventId]);

  const loadCompetitions = async () => {
    try {
      const { data, errors } = await client.models.Event.list({
        filter: {
          eventType: { eq: 'COMPETITION' },
          isActive: { eq: true },
        },
      });
      if (errors) {
        console.error('Error loading competitions:', errors);
      }
      const comps = (data || []).filter((c: any) => deriveLifecycle(c, new Date()).catchesOpen);
      setCompetitions(comps);
      if (comps.length === 1) {
        setSelectedEventId(comps[0].id);
      }
    } catch (err) {
      console.error('Error loading competitions:', err);
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message: string, type: 'success' | 'error' | 'warning') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const legalSize = species ? LEGAL_CATCH_SIZES[species] : undefined;
  const lengthNum = parseFloat(lengthCm);
  const isBelowLegal = legalSize !== undefined && !isNaN(lengthNum) && lengthNum > 0 && lengthNum < legalSize;
  const isBlockedSubmission = isBelowLegal && keptOrReleased === 'KEPT';
  const isOverMax = !isNaN(lengthNum) && isOverMaxLength(lengthNum);
  const speciesLabel = SPECIES_OPTIONS.find((s) => s.value === species)?.label || species;

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setParticipantEmail('');
    setSpecies('');
    setLengthCm('');
    setPhoto(null);
    setPhotoPreview(null);
    setKeptOrReleased('RELEASED');
    setTimeOfCatch(formatDateForInput(new Date()));
    setSubmitted(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e?: React.FormEvent, lengthConfirmed = false) => {
    e?.preventDefault();

    if (!selectedEventId || !participantEmail || !species || !lengthCm || !photo) {
      showToast('Please fill in all required fields and attach a photo.', 'error');
      return;
    }
    if (isNaN(lengthNum) || lengthNum <= 0) {
      showToast('Please enter a valid length in centimetres.', 'error');
      return;
    }
    if (isBlockedSubmission) {
      showToast(`This fish is below the legal catch size of ${legalSize}cm for ${species}. It must be marked as released.`, 'error');
      return;
    }
    if (isOverMax) {
      showToast(MAX_CATCH_LENGTH_MESSAGE, 'error');
      return;
    }
    if (!lengthConfirmed && isLengthUnusual(species, lengthNum)) {
      setShowLengthConfirm(true);
      return;
    }

    setSubmitting(true);
    try {
      // Compress photo client-side
      const imageCompression = (await import('browser-image-compression')).default;
      const compressed = await imageCompression(photo, {
        maxSizeMB: 2,
        maxWidthOrHeight: 2048,
        useWebWorker: true,
      });

      // 1. Get presigned upload URL (apiKey auth)
      const fileName = compressed.name || photo.name || 'catch-photo.jpg';
      const contentType = compressed.type || photo.type || 'image/jpeg';

      // Sunday hardening (G2): trimmed only. The server tries this as typed
      // and then lower-cased, so both older mixed-case registrations and new
      // lower-case ones match. Lower-casing here would stop the older ones.
      const guestEmail = participantEmail.trim();
      const uploadResult = await (client.queries as any).generateCatchUploadUrl({
        eventId: selectedEventId,
        fileName,
        contentType,
        participantEmail: guestEmail,
      });

      const uploadData = parseLambdaResponse<{ uploadUrl?: string; s3Key?: string }>(uploadResult?.data);
      if (!uploadData?.uploadUrl || !uploadData?.s3Key) {
        throw new Error('Failed to get upload URL. Please check your email is registered for this competition.');
      }

      // 2. Upload compressed photo to S3
      const uploadResponse = await fetch(uploadData.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: compressed,
      });
      if (!uploadResponse.ok) {
        throw new Error('Photo upload failed. Please try again.');
      }

      // 3. Submit catch (apiKey auth)
      const catchTime = new Date(timeOfCatch).toISOString();
      const result = await (client.mutations as any).submitCatchGuest({
        eventId: selectedEventId,
        species,
        lengthCm: lengthNum,
        photoS3Key: uploadData.s3Key,
        keptOrReleased,
        timeOfCatch: catchTime,
        participantEmail: guestEmail,
      });

      if (result?.errors && result.errors.length > 0) {
        throw new Error(sanitise(result.errors[0].message));
      }

      /*
        Business failures arrive in a 200 body as { success: false, error }, not
        in result.errors — see MemberCatchSubmission for the full reasoning and
        the dev evidence. Without this the guest is shown "Catch Submitted!"
        while the server has discarded the catch.
      */
      const payload = parseLambdaResponse<{ success?: boolean; error?: string }>(result?.data);
      if (payload && payload.success === false) {
        throw new Error(
          sanitise(payload.error || 'Your catch could not be submitted. Please speak to an official.'),
        );
      }

      setSubmitted(true);
      showToast('Catch submitted successfully!', 'success');
    } catch (err: any) {
      console.error('Error submitting catch:', err);
      showToast(sanitise(err.message || 'Failed to submit catch. Please try again.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 py-12 px-4 flex items-center justify-center">
        <div className="text-gray-600">Loading competitions...</div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 py-8 px-4">
        <div className="max-w-lg mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="text-6xl mb-4">🎣</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Catch Submitted!</h2>
            <p className="text-gray-600 mb-6">Your catch has been recorded and counts towards the results. The top 6 in each category are checked by an official before prizes are awarded.</p>
            <button
              onClick={resetForm}
              className="w-full px-6 py-3 bg-fishing-blue text-white rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Submit Another Catch
            </button>
          </div>
        </div>
      </div>
    );
  }

  /*
    Submission-window gate — guest counterpart of the one in
    MemberCatchSubmission. #134: derived from the one shared lifecycle
    function, matching the server's deriveLifecycle(evt, now).catchesOpen
    gate exactly, instead of a local window calculation.

    Only applied once a competition is actually selected — before that the
    picker must still render so a guest can choose one.
  */
  const guestLifecycle = selectedEventId && timingLoaded && eventTiming
    ? deriveLifecycle(eventTiming, nowTick)
    : null;

  if (guestLifecycle && !guestLifecycle.catchesOpen) {
    const startsAt = eventTiming?.competitionStartTime ? new Date(eventTiming.competitionStartTime) : null;
    const endsAt = eventTiming?.competitionEndTime ? new Date(eventTiming.competitionEndTime) : null;
    const clubTime = (d: Date) =>
      formatClubDateTime(d, {
        timeZone: CLUB_TIMEZONE,
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });

    const reason = guestLifecycle.catchReason;
    const heading =
      reason === 'NOT_STARTED'
        ? 'Catch submission is not open yet'
        : reason === 'ENDED' || reason === 'ADJUDICATED'
          ? 'Catch submission has closed'
          : reason === 'CANCELLED'
            ? 'This competition has been cancelled'
            : 'Catch submission is unavailable';

    const detail =
      reason === 'NOT_STARTED' && startsAt
        ? `Submissions open when fishing starts, ${clubTime(startsAt)}. You can take your photos before then and upload them once it opens.`
        : (reason === 'ENDED' || reason === 'ADJUDICATED') && endsAt
          ? `Submissions closed ${guestLifecycle.graceMinutes} minutes after fishing ended at ${clubTime(endsAt)}. Please speak to an official if you have a catch to record.`
          : reason === 'CANCELLED'
            ? catchRefusalMessage(reason)
            : 'This competition has no fishing start and end time set, so submissions cannot be accepted. Please let an official know.';

    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 py-8 px-4">
        <div className="max-w-lg mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="text-4xl mb-3" aria-hidden="true">🎣</div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">{heading}</h1>
            <p className="text-sm text-gray-600">{detail}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100 py-8 px-4">
      {toast && (
        <div role="alert" aria-live="assertive" className={`fixed top-4 left-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm transition-all ${
          toast.type === 'success' ? 'bg-green-600' : toast.type === 'warning' ? 'bg-yellow-600' : 'bg-red-600'
        }`}>
          {toast.message}
        </div>
      )}

      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-4">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-2xl font-bold text-gray-900">Submit Catch</h1>
            <button
              onClick={() => { window.history.pushState({}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate')); }}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Back to home"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-gray-600 text-sm">
            Guest catch submission — enter your registered email and catch details below.
          </p>
          <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">📋 All catches are reviewed and approved by an admin. Photos are verified using AI to help ensure fair competition.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Competition Selection */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            {preScopedEventId ? (
              <div>
                <span className="block text-sm font-semibold text-gray-900 mb-1">Competition</span>
                <p className="text-lg font-semibold text-fishing-blue">
                  {preScopedCompTitle || 'Loading competition…'}
                </p>
              </div>
            ) : (
              <>
                <label htmlFor="competition" className="block text-sm font-semibold text-gray-900 mb-3">
                  Competition *
                </label>
                {competitions.length === 0 ? (
                  <p className="text-gray-500 text-sm">No open competitions at the moment.</p>
                ) : (
                  <select
                    id="competition"
                    value={selectedEventId}
                    onChange={(e) => setSelectedEventId(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-fishing-blue focus:border-fishing-blue bg-white"
                  >
                    <option value="">Select a competition</option>
                    {competitions.map((comp: any) => (
                      <option key={comp.id} value={comp.id}>
                        {comp.title} — {formatClubDate(comp.eventDate)}
                      </option>
                    ))}
                  </select>
                )}
              </>
            )}
          </div>

          {/* Email */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-3">
              Registered Email *
            </label>
            <input
              id="email"
              type="email"
              value={participantEmail}
              onChange={(e) => setParticipantEmail(e.target.value)}
              placeholder="Enter the email you checked in with"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-fishing-blue focus:border-fishing-blue"
            />
            <p className="text-gray-500 text-xs mt-2">Must match the email used during competition check-in.</p>
          </div>

          {/* Photo Capture */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <label className="block text-sm font-semibold text-gray-900 mb-3">Catch Photo *</label>
            {photoPreview ? (
              <div className="relative mb-3">
                <img src={photoPreview} alt="Catch preview" className="w-full rounded-lg object-cover max-h-64" />
                <button
                  type="button"
                  onClick={() => { setPhoto(null); setPhotoPreview(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                  className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1.5 shadow-lg"
                  aria-label="Remove photo"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full py-8 border-2 border-dashed border-blue-300 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors flex flex-col items-center justify-center"
              >
                <svg className="h-12 w-12 text-fishing-blue mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-fishing-blue font-medium text-lg">Take Photo or Upload</span>
                <span className="text-gray-500 text-sm mt-1">Show fish on brag mat with event sticker</span>
              </button>
            )}
            {/*
              No `capture` attribute — see MemberCatchSubmission for the full
              reasoning. `capture="environment"` forced the rear camera and hid
              the photo library, blocking submission of a shot already taken.
              Removed 19 Sep 2026; the OS picker still offers the camera.
            */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              className="hidden"
            />
          </div>

          {/* Species */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <label className="block text-sm font-semibold text-gray-900 mb-3">Species *</label>
            <div className="grid grid-cols-2 gap-3">
              {SPECIES_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSpecies(opt.value)}
                  className={`py-3 px-4 rounded-lg text-sm font-medium border-2 transition-colors ${
                    species === opt.value
                      ? 'border-fishing-blue bg-blue-50 text-fishing-blue'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {species === 'OTHER' && (
              <p className="mt-3 text-xs text-gray-600">
                "Other" species are subject to NSW DPI size limits but may not be eligible for competition prizes.
              </p>
            )}
          </div>

          {/* Length */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <label htmlFor="lengthCm" className="block text-sm font-semibold text-gray-900 mb-3">
              Length (cm) *
            </label>
            <input
              id="lengthCm"
              type="number"
              inputMode="decimal"
              min="0"
              step="0.1"
              ref={lengthInputRef}
              value={lengthCm}
              onChange={(e) => setLengthCm(e.target.value)}
              aria-invalid={isOverMax || undefined}
              aria-describedby={isOverMax ? 'lengthCm-error' : undefined}
              placeholder="Enter length in centimetres"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-fishing-blue focus:border-fishing-blue"
            />
            {isOverMax && (
              <p id="lengthCm-error" role="alert" className="mt-3 text-sm font-medium text-red-700">
                {MAX_CATCH_LENGTH_MESSAGE}
              </p>
            )}
            {isBelowLegal && (
              <div className={`mt-3 p-3 rounded-lg ${isBlockedSubmission ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                <p className={`text-sm font-medium ${isBlockedSubmission ? 'text-red-800' : 'text-yellow-800'}`}>
                  {isBlockedSubmission
                    ? `⛔ This fish is below the legal catch size of ${legalSize}cm for ${SPECIES_OPTIONS.find(s => s.value === species)?.label}. It must be marked as released.`
                    : `⚠️ This fish is below the legal catch size of ${legalSize}cm for ${SPECIES_OPTIONS.find(s => s.value === species)?.label}. Please ensure it was released.`}
                </p>
              </div>
            )}
          </div>

          {/* Kept/Released */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <label className="block text-sm font-semibold text-gray-900 mb-3">Kept or Released *</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setKeptOrReleased('RELEASED')}
                className={`py-3 px-4 rounded-lg text-sm font-medium border-2 transition-colors ${
                  keptOrReleased === 'RELEASED'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                }`}
              >
                🐟 Released
              </button>
              <button
                type="button"
                onClick={() => setKeptOrReleased('KEPT')}
                className={`py-3 px-4 rounded-lg text-sm font-medium border-2 transition-colors ${
                  keptOrReleased === 'KEPT'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                }`}
              >
                🪣 Kept
              </button>
            </div>
          </div>

          {/* Time of Catch */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <label htmlFor="timeOfCatch" className="block text-sm font-semibold text-gray-900 mb-3">
              Time of Catch
            </label>
            <input
              id="timeOfCatch"
              type="datetime-local"
              value={timeOfCatch}
              onChange={(e) => setTimeOfCatch(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-fishing-blue focus:border-fishing-blue"
            />
          </div>

          {/* AI processing disclosure */}
          <p className="text-xs text-red-600 text-center">
            Photos are processed by AI for verification purposes.
          </p>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting || isBlockedSubmission || isOverMax || !selectedEventId || !participantEmail || !species || !lengthCm || !photo}
            className="w-full py-4 bg-fishing-blue text-white rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed shadow-lg"
          >
            {submitting ? 'Submitting...' : 'Submit Catch'}
          </button>
        </form>
        <LengthConfirmDialog
          open={showLengthConfirm}
          lengthCm={lengthNum}
          speciesLabel={speciesLabel}
          onFixLength={() => {
            setShowLengthConfirm(false);
            lengthInputRef.current?.focus();
          }}
          onConfirm={() => {
            setShowLengthConfirm(false);
            handleSubmit(undefined, true);
          }}
        />
      </div>
    </div>
  );
};

export default GuestCatchSubmission;
